<template>
    <section v-if="order" class="od-hero-card">
        <div class="od-hero-glow" aria-hidden="true"></div>

        <div class="od-hero-top">
            <div class="od-hero-main">
                <h2 class="od-hero-title">{{ headline.title }}</h2>
                <p v-if="headline.sub" class="od-hero-sub">{{ headline.sub }}</p>
            </div>

            <div v-if="destination" class="od-hero-destination">
                <span class="od-hero-pin">
                    <MapPin :size="14" />
                </span>
                <span>{{ destination }}</span>
            </div>

            <div class="od-hero-placed">
                <div class="od-hero-label">RECEIVED</div>
                <div class="od-hero-value">{{ placedAtLabel }}</div>
            </div>
        </div>

        <div class="od-hero-stats">
            <span class="od-hero-stat">
                <span class="od-hero-stat-label">Shipments</span>
                <span class="od-hero-stat-value">{{ packagesTile.value }}</span>
                <span v-if="packagesTile.sub" class="od-hero-stat-dot">&middot;</span>
                <span v-if="packagesTile.sub" class="od-hero-stat-sub">{{ packagesTile.sub }}</span>
            </span>

            <span class="od-hero-divider" aria-hidden="true"></span>

            <span class="od-hero-stat">
                <span class="od-hero-stat-label">{{ unitsTile.label }}</span>
                <span class="od-hero-stat-value">{{ unitsTile.value }}</span>
            </span>

            <span class="od-hero-divider" aria-hidden="true"></span>

            <span class="od-hero-stat">
                <span class="od-hero-stat-label">Order value</span>
                <span class="od-hero-stat-value">{{ orderValueTile.value }}</span>
                <span v-if="orderValueTile.sub" class="od-hero-stat-dot">&middot;</span>
                <span v-if="orderValueTile.sub" class="od-hero-stat-sub">{{ orderValueTile.sub }}</span>
            </span>
        </div>
    </section>
</template>

<script setup>
import { computed } from "vue";
import { MapPin } from "lucide-vue-next";

const props = defineProps({
    order: {
        type: Object,
        default: () => null,
    },
    shipments: {
        type: Array,
        default: () => [],
    },
});

const toNumber = (value) => {
    const n = Number(value || 0);
    return Number.isFinite(n) ? n : 0;
};

const money = (value) => {
    return new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
        maximumFractionDigits: 2,
    }).format(toNumber(value));
};

const formatDateTime = (value) => {
    if (!value) return "-";
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return String(value);
    const datePart = new Intl.DateTimeFormat("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
    }).format(d);
    const timePart = new Intl.DateTimeFormat("en-US", {
        hour: "numeric",
        minute: "2-digit",
    }).format(d);
    return `${datePart} · ${timePart}`;
};

const totalUnits = computed(() =>
    toNumber(
        props.order?.OrderLineItemsQuantity ||
        props.order?.order_line_items?.reduce((sum, item) => sum + toNumber(item.qty), 0)
    )
);

const shipmentStats = computed(() => {
    const list = Array.isArray(props.shipments) ? props.shipments : [];
    const delivered = list.filter((s) => /delivered|picked up/i.test(String(s.status || ""))).length;
    const inTransit = list.filter((s) => /transit/i.test(String(s.status || ""))).length;
    const shipped = list.filter((s) => /ship/i.test(String(s.status || "")) && !/transit/i.test(String(s.status || ""))).length;
    const totalShipmentUnits = list.reduce((sum, s) => sum + toNumber(s.totalUnits), 0);
    const unassigned = Math.max(totalUnits.value - totalShipmentUnits, 0);
    const pickupOnly = list.length > 0 && list.every((s) => /pickup|medusa/i.test(String(s.carrier || "")));

    return {
        total: list.length,
        delivered,
        inTransit,
        shipped,
        totalShipmentUnits,
        unassigned,
        pickupOnly,
    };
});

const phase = computed(() => {
    const stats = shipmentStats.value;

    if (!stats.total) return "received";
    if (stats.pickupOnly) {
        return stats.delivered === stats.total ? "pickup_only" : "pickup_awaiting";
    }
    if (stats.delivered === stats.total) return "complete";
    if (stats.delivered > 0) return "partial_delivered";
    if (stats.unassigned > 0) return "packing";
    if (stats.inTransit === 0 && stats.shipped > 0) return "packed";
    return "in_motion";
});

const headline = computed(() => {
    const stats = shipmentStats.value;
    const p = phase.value;

    if (p === "received") {
        return {
            title: "Order received - preparing to ship",
            sub: `${totalUnits.value} units · awaiting fulfillment`,
        };
    }

    if (p === "packing") {
        return {
            title: `${stats.total} package${stats.total === 1 ? "" : "s"} packed - more to come`,
            sub: `${stats.totalShipmentUnits} of ${totalUnits.value} units packed · ${stats.unassigned} still being prepared`,
        };
    }

    if (p === "packed") {
        return {
            title: "All packed - awaiting carrier handoff",
            sub: `${stats.total} package${stats.total === 1 ? "" : "s"} ready to ship`,
        };
    }

    if (p === "in_motion") {
        return {
            title: stats.total === 1 ? "Your package is on the way" : `${stats.total} packages on their way`,
            sub: null,
        };
    }

    if (p === "partial_delivered") {
        const moving = stats.total - stats.delivered;
        return {
            title: `${stats.delivered} of ${stats.total} packages delivered · ${moving} in transit`,
            sub: null,
        };
    }

    if (p === "complete") {
        return {
            title: stats.total === 1 ? "Delivered" : `All ${stats.total} packages delivered`,
            sub: "Thanks for your order",
        };
    }

    if (p === "pickup_only") {
        return {
            title: "Order picked up",
            sub: null,
        };
    }

    return {
        title: "Order details",
        sub: null,
    };
});

const packagesTile = computed(() => {
    const stats = shipmentStats.value;
    const p = phase.value;

    if (p === "received") return { value: "0", sub: "to be created" };
    if (p === "packing") return { value: String(stats.total), sub: "still packing" };
    if (p === "packed") return { value: String(stats.total), sub: "awaiting handoff" };
    if (p === "complete") return { value: String(stats.total), sub: "all delivered" };
    if (p === "pickup_only") return { value: String(stats.total), sub: "picked up" };

    const parts = [];
    if (stats.delivered) parts.push(`${stats.delivered} delivered`);
    const moving = stats.inTransit + stats.shipped;
    if (moving) parts.push(`${moving} in transit`);
    return { value: String(stats.total), sub: parts.join(" · ") || "in motion" };
});

const unitsTile = computed(() => {
    const stats = shipmentStats.value;
    if (stats.unassigned > 0) {
        return { label: "Units packed", value: `${stats.totalShipmentUnits} / ${totalUnits.value}` };
    }
    return { label: "Units", value: String(totalUnits.value) };
});

const orderValueTile = computed(() => {
    const total = toNumber(props.order?.orderTotal || props.order?.order_total);
    const paid = toNumber(props.order?.total_paid)
        || toNumber(props.order?.invoice?.[0]?.PaidAmount)
        || (Array.isArray(props.order?.payments) ? props.order.payments.reduce((s, p) => s + toNumber(p.amount), 0) : 0);
    const balance = Math.max(total - paid, 0);

    if (!total) {
        return { value: "$0.00", sub: null };
    }

    if (balance === 0 && paid > 0) {
        return { value: money(total), sub: "Paid in full" };
    }

    return { value: money(total), sub: `${money(balance || total)} due` };
});

const destination = computed(() => {
    const shipping = props.order?.shipping_address;
    if (!shipping) return "";
    const city = shipping.city || "";
    const state = shipping.state_code || shipping.state || "";
    return [city, state].filter(Boolean).join(", ");
});

const placedAtLabel = computed(() => formatDateTime(props.order?.created_at));
</script>

<style scoped>
.od-hero-card {
    /* Match the representative card direction with a single-family gradient. */
    background: linear-gradient(135deg, #3e3c50 0%, #474557 58%, #545068 100%);
    border-radius: 14px;
    color: #fff;
    padding: 18px 22px;
    position: relative;
    overflow: hidden;
    border: 1px solid rgba(255, 255, 255, 0.16);
}

.od-hero-glow {
    position: absolute;
    top: -80px;
    right: -60px;
    width: 240px;
    height: 240px;
    border-radius: 50%;
    background: radial-gradient(circle, rgba(255, 255, 255, 0.16) 0%, rgba(255, 255, 255, 0) 70%);
    pointer-events: none;
}

.od-hero-top {
    position: relative;
    z-index: 1;
    display: flex;
    align-items: flex-start;
    gap: 24px;
}

.od-hero-main {
    min-width: 0;
}

.od-hero-title {
    font-size: 24px;
    font-weight: 600;
    letter-spacing: -0.015em;
    line-height: 1.15;
}

.od-hero-sub {
    margin-top: 4px;
    font-size: 13px;
    opacity: 0.72;
}

.od-hero-destination {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-left: auto;
    opacity: 0.95;
    align-self: center;
    font-size: 14px;
    font-weight: 700;
    letter-spacing: -0.005em;
}

.od-hero-pin {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
    border-radius: 9999px;
    background: rgba(255, 255, 255, 0.16);
}

.od-hero-placed {
    margin-left: auto;
    text-align: right;
    flex-shrink: 0;
}

.od-hero-label {
    font-size: 10px;
    color: rgba(255, 255, 255, 0.7);
    text-transform: uppercase;
    letter-spacing: 0.1em;
}

.od-hero-value {
    margin-top: 3px;
    font-size: 13px;
    font-weight: 600;
}

.od-hero-stats {
    margin-top: 14px;
    padding-top: 12px;
    border-top: 1px solid rgba(255, 255, 255, 0.2);
    position: relative;
    z-index: 1;
    display: flex;
    align-items: baseline;
    flex-wrap: wrap;
    row-gap: 6px;
    font-size: 13px;
}

.od-hero-stat {
    display: inline-flex;
    align-items: baseline;
    gap: 6px;
    min-width: 0;
}

.od-hero-stat-label {
    font-size: 10px;
    font-weight: 700;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    color: rgba(255, 255, 255, 0.7);
}

.od-hero-stat-value {
    font-size: 15px;
    font-weight: 700;
    letter-spacing: -0.01em;
}

.od-hero-stat-dot {
    font-size: 12px;
    opacity: 0.4;
}

.od-hero-stat-sub {
    font-size: 12px;
    opacity: 0.6;
}

.od-hero-divider {
    display: inline-block;
    width: 1px;
    height: 16px;
    background: rgba(255, 255, 255, 0.24);
    margin: 0 14px;
    align-self: center;
}

@media (max-width: 1024px) {
    .od-hero-top {
        flex-wrap: wrap;
        gap: 12px;
    }

    .od-hero-title {
        font-size: 20px;
    }

    .od-hero-destination {
        margin-left: 0;
    }

    .od-hero-placed {
        margin-left: 0;
    }
}

@media (max-width: 640px) {
    .od-hero-card {
        padding: 14px 14px;
    }

    .od-hero-title {
        font-size: 18px;
    }

    .od-hero-stats {
        gap: 6px;
    }

    .od-hero-divider {
        display: none;
    }
}
</style>
